import "@xyflow/react/dist/style.css";
import "pages/Sales/SUtilities/Products/Workflows/workflow.scss";
import { Background, Controls, MarkerType, ReactFlow } from "@xyflow/react";
import Spinner from "components/Spinner";
import WorkflowHistoryModal, { stationText, whenText } from "components/Workflow/WorkflowHistoryModal";
import { setNotification } from "features/Notification/notificationSlice";
import { WorkflowContext, edgeTypes, nodeTypes } from "pages/Sales/SUtilities/Products/Workflows/WorkflowNodes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * W-Order -> Workflow tab (user, 2026-11-02). Each product goes through its
 * own product type's workflow, so each gets its own drawing with where it
 * stands, the last thing done and a History button. Done / Undo work here
 * too (same rules and permission as the Station Board).
 *
 * An accordion, one product open at a time, each colour coded (user,
 * 2026-11-02): green done, amber in progress, grey no workflow.
 */

// Colour of a product's row: { bar, bg, badge }.
const LOOKS = {
  done: { bar: "#16a34a", bg: "#f0fdf4", badge: "bg-success" },
  working: { bar: "#f59e0b", bg: "#fffbeb", badge: "bg-warning text-dark" },
  none: { bar: "#9ca3af", bg: "#f9fafb", badge: "bg-secondary" },
};
const lookOf = (p) => LOOKS[!p.hasWorkflow ? "none" : p.complete ? "done" : "working"];

const edgeLook = {
  type: "removable",
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16, color: "#9ca3af" },
  style: { strokeWidth: 1.5, stroke: "#9ca3af" },
};

function ProductFlow({ product, stations, onMark }) {
  const statuses = useMemo(() => new Map(Object.entries(product.statuses || {})), [product.statuses]);
  const ctx = useMemo(
    () => ({
      mode: "test",
      statuses,
      stations,
      setDone: (nodeId, done) => onMark(product, nodeId, done),
      removeEdge: () => {},
    }),
    [statuses, stations, onMark, product],
  );
  const edges = useMemo(() => product.flow.edges.map((e) => ({ ...e, ...edgeLook })), [product.flow.edges]);
  return (
    <div style={{ height: 520 }} className="border rounded bg-light">
      <WorkflowContext.Provider value={ctx}>
        <ReactFlow
          nodes={product.flow.nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          deleteKeyCode={null}
          fitView
          fitViewOptions={{ padding: 0.1, maxZoom: 1 }}
          minZoom={0.2}
          maxZoom={1.5}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={18} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </WorkflowContext.Provider>
    </div>
  );
}

export default function WOWorkflow({ selectedWorkOrder }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [stations, setStations] = useState(new Map());
  const [history, setHistory] = useState(null);
  const [openId, setOpenId] = useState(null);

  const notify = useCallback(
    (message, type = "success") => dispatch(setNotification({ message, type })),
    [dispatch],
  );

  const load = useCallback(async () => {
    if (!selectedWorkOrder?.id) return;
    try {
      setLoading(true);
      const { data } = await API.get(`/work-order/${selectedWorkOrder.id}/workflows`, {
        headers: authHeader(),
      });
      if (!data?.success) throw new Error(data?.message || "Failed");
      const list = data.data || [];
      setProducts(list);
      // Open the first product still going through its workflow.
      setOpenId((cur) => cur ?? list.find((p) => p.hasWorkflow && !p.complete)?.productId ?? null);
      setStations(new Map((data.stations || []).map((s) => [Number(s.id), s])));
    } catch (err) {
      notify(err?.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [selectedWorkOrder?.id, notify]);

  useEffect(() => {
    load();
  }, [load]);

  const onMark = useCallback(
    async (product, nodeId, done) => {
      try {
        setLoading(true);
        const { data } = await API.post(
          "/station-board/mark",
          { productId: product.productId, nodeId, done },
          { headers: authHeader() },
        );
        if (!data?.success) throw new Error(data?.message || "Failed");
        notify(data.message);
        await load();
      } catch (err) {
        notify(err?.response?.data?.message || err.message, "error");
        setLoading(false);
      }
    },
    [load, notify],
  );

  // "At Print Film, Shirt Receiving" -- the stations a product is working at.
  const whereText = (p) => {
    if (!p.hasWorkflow) return null;
    if (p.complete) return "Through every station";
    const at = p.flow.nodes
      .filter((n) => n.type === "step" && p.statuses?.[n.id]?.status === "working")
      .map((n) => stations.get(Number(n.data?.stationId))?.name || "Station removed");
    return at.length ? `At ${at.join(", ")}` : "Waiting";
  };

  return (
    <div>
      {loading && <Spinner />}
      {selectedWorkOrder?.WOStatus === "pending" && (
        <div className="alert alert-info py-1 px-2 small">
          Products start their workflows when the order is Approved.
        </div>
      )}
      {!loading && !products.length && <div className="text-muted p-2">No products on this order.</div>}
      {products.map((p) => {
        const last = p.history?.[p.history.length - 1];
        const look = lookOf(p);
        const open = openId === p.productId && p.hasWorkflow;
        return (
          <div
            key={p.productId}
            className="card border-0 shadow-sm mb-2"
            style={{ borderLeft: `5px solid ${look.bar}`, overflow: "hidden" }}
          >
            <div
              className="card-header d-flex flex-wrap align-items-center gap-2 border-0"
              style={{ background: look.bg, cursor: p.hasWorkflow ? "pointer" : "default" }}
              onClick={() => p.hasWorkflow && setOpenId(open ? null : p.productId)}
            >
              {p.hasWorkflow && (
                <i className={`mdi ${open ? "mdi-chevron-down" : "mdi-chevron-right"} fs-5`} />
              )}
              <span className="badge bg-light text-dark border">#{p.productId}</span>
              <b>{p.productStyle || "-"}</b>
              <span className="text-muted small">
                {[p.productColor, p.productType].filter(Boolean).join(" · ")}
              </span>
              <span className={`badge ${look.badge}`}>
                {p.hasWorkflow ? (
                  <>
                    {p.complete && <i className="mdi mdi-check me-1" />}
                    {whereText(p)}
                  </>
                ) : (
                  "No workflow for this product type"
                )}
              </span>
              <div className="ms-auto d-flex align-items-center gap-2 small">
                {last && (
                  <span className="text-muted">
                    Last: {stationText(last, stations)} {last.action === "done" ? "done" : "undone"} by{" "}
                    {last.userName || "-"}, {whenText(last.at)}
                  </span>
                )}
                {p.hasWorkflow && (
                  <button
                    type="button"
                    className="btn btn-sm bg-white border"
                    onClick={(e) => {
                      e.stopPropagation();
                      setHistory({ title: `#${p.productId} ${p.productStyle || ""}`, history: p.history });
                    }}
                  >
                    <i className="mdi mdi-history" /> History
                  </button>
                )}
              </div>
            </div>
            {open && (
              <div className="card-body p-2">
                <ProductFlow product={p} stations={stations} onMark={onMark} />
              </div>
            )}
          </div>
        );
      })}

      <WorkflowHistoryModal
        show={!!history}
        onHide={() => setHistory(null)}
        title={history?.title}
        history={history?.history}
        stations={stations}
      />
    </div>
  );
}
